import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  Building, 
  Building2,
  Users, 
  Euro, 
  Calendar,
  TrendingUp,
  Target,
  Phone,
  Mail,
  MapPin,
  Globe,
  User,
  Clock,
  MessageSquare,
  Activity,
  ChevronRight,
  Plus,
  Edit,
  Search,
  UserPlus,
  Check,
  Save,
  Package,
  Layers,
  Code,
  Wrench,
  FileText,
  CheckSquare,
  Square,
  ListChecks,
  Briefcase,
  Upload
} from 'lucide-react';
import { Opportunity, Lead, Product, Company, Task, BUSINESS_AREAS, BusinessArea, User as UserType } from '../../types';
import { formatDateSafe, toISODateString } from '../../utils/dateHelpers';
import { InlineDatePicker } from '../shared/InlineDatePicker';
import { productsService } from '../../services/productsService';
import { tasksService } from '../../services/tasksService';
import QuickAddTask from '../Tasks/QuickAddTask';
import { opportunitiesService } from '../../services/opportunitiesService';
import { pricingComponentsFor, serviceCategoryOf } from '../../utils/productPricing';
import { companiesService } from '../../services/companiesService';
import { usersService } from '../../services/usersService';
import { engagementsService } from '../../services/engagementsService';
import { toastService } from '../../services/toastService';
import OpportunityNotes from './OpportunityNotes';

interface OpportunityDetailProps {
  opportunity: Opportunity | null;
  allLeads: Lead[];
  isOpen: boolean;
  onClose: () => void;
  onStageChange: (opportunityId: string, newStage: Opportunity['stage']) => void;
  onAddLeadToOpportunity: (opportunityId: string, leadId: string) => void;
  onUpdateOpportunity?: (opportunityId: string, updates: Partial<Opportunity>) => void;
  onCreateOffer?: (opportunity: Opportunity) => void;
  onUploadDocument?: (opportunity: Opportunity) => void;
  orgId?: string;
  currentUserId?: string;
  currentUserName?: string;
  currentUserPhoto?: string;
  /** When true, renders as inline side panel instead of overlay modal */
  inline?: boolean;
}

const OpportunityDetail: React.FC<OpportunityDetailProps> = ({
  opportunity,
  allLeads,
  isOpen,
  onClose,
  orgId,
  onStageChange,
  onAddLeadToOpportunity,
  onUpdateOpportunity,
  onCreateOffer,
  onUploadDocument,
  currentUserId = '',
  currentUserName = 'User',
  currentUserPhoto,
  inline = false
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'leads' | 'company' | 'product' | 'notes' | 'tasks'>('overview');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [oppTasks, setOppTasks] = useState<Task[]>([]);

  const handleEscapeKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  // Load tasks for this opportunity
  useEffect(() => {
    const loadOppTasks = async () => {
      if (!opportunity?.id || !opportunity?.orgId) return;
      try {
        const allTasks = await tasksService.getTasks(currentUserId, opportunity.orgId || '');
        setOppTasks(allTasks.filter(t => t.opportunityId === opportunity.id));
      } catch (error) {
        console.error('Error loading opportunity tasks:', error);
      }
    };
    if (isOpen) loadOppTasks();
  }, [opportunity?.id, isOpen, currentUserId]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState<{ title: string; value: string; probability: string; expectedCloseDate: string; companyId: string; assignedTo: string; businessArea: string }>({ title: '', value: '', probability: '', expectedCloseDate: '', companyId: '', assignedTo: '', businessArea: '' });
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>(opportunity?.productId || '');
  const [isChangingProduct, setIsChangingProduct] = useState(false);
  // Recompute opportunity value from the product's pricing components (rate card level × days / package / term).
  const [pricingBasisIdx, setPricingBasisIdx] = useState(0);
  const [pricingUnits, setPricingUnits] = useState(1);
  const [pricingMonths, setPricingMonths] = useState<number>(Number((opportunity as any)?.billingTermMonths) || 12);
  const [applyingValue, setApplyingValue] = useState(false);

  // Load companies, products, and users when modal opens
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const orgId = await usersService.getCurrentUserOrgId();
        if (orgId) {
          const companies = await companiesService.getCompanies(orgId);
          setAllCompanies(companies);
          
          const products = await productsService.list();
          setAllProducts(products);

          const users = await usersService.getUsers(orgId);
          setAllUsers(users);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    if (isOpen) {
      loadData();
      setSelectedProductId(opportunity?.productId || '');
    }
  }, [isOpen, opportunity?.productId]);

  // Initialize edit fields when opening or switching (must be called on every render)
  React.useEffect(() => {
    if (opportunity) {
      setEditData({
        title: opportunity.title,
        value: String(opportunity.value || ''),
        probability: String(opportunity.probability || ''),
        expectedCloseDate: toISODateString(opportunity.expectedCloseDate),
        companyId: opportunity.companyId || '',
        assignedTo: opportunity.assignedTo || '',
        businessArea: opportunity.businessArea || ''
      });
    }
  }, [opportunity?.id, isOpen]);

  if (!isOpen || !opportunity) return null;

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      prospecting: 'bg-bg-surface text-txt',
      qualification: 'bg-info-soft text-info',
      proposal: 'bg-warning-soft text-warning',
      negotiation: 'bg-accent-soft text-accent',
      closing: 'bg-success-soft text-success',
      won: 'bg-success-soft text-success',
      lost: 'bg-danger-soft text-danger',
    };
    return colors[stage] || 'bg-bg-surface text-txt';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    const icons: Record<string, any> = {
      lead_created: Users,
      lead_stage_changed: TrendingUp,
      email_sent: Mail,
      email_received: Mail,
      call_made: Phone,
      meeting_scheduled: Calendar,
      proposal_sent: Target,
      stage_changed: Activity,
      note_added: MessageSquare,
    };
    return icons[type] || Activity;
  };

  // Ensure allLeads is always an array
  const safeAllLeads = allLeads || [];
  
  const leadsInOpportunity = (opportunity.leads && opportunity.leads.length > 0)
    ? opportunity.leads
    : safeAllLeads.filter(l => opportunity.leadIds?.includes(l.id));

  const availableLeads = safeAllLeads.filter(lead => {
    const ids = opportunity.leadIds || [];
    return !ids.includes(lead.id);
  });

  const filteredAvailableLeads = availableLeads.filter(lead =>
    (lead.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddSelectedLeads = async () => {
    console.log('Adding leads:', selectedLeads);
    
    // Add all selected leads
    for (const leadId of selectedLeads) {
      await onAddLeadToOpportunity(opportunity.id, leadId);
    }
    
    // Clear selection and close modal
    setSelectedLeads([]);
    setShowAddLeadModal(false);
    setSearchTerm('');
    
    // Show success feedback
    console.log(`Successfully added ${selectedLeads.length} lead(s) to opportunity`);
  };

  const toggleLeadSelection = (leadId: string) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const stages: Array<{ key: Opportunity['stage']; label: string }> = [
    { key: 'prospecting', label: 'Prospecting' },
    { key: 'qualification', label: 'Qualification' },
    { key: 'proposal', label: 'Proposal' },
    { key: 'negotiation', label: 'Negotiation' },
    { key: 'closing', label: 'Closing' },
    { key: 'won', label: 'Won' },
    { key: 'lost', label: 'Lost' }
  ];


  const handleProductChange = async (productId: string) => {
    if (!opportunity || !productId) return;
    
    const product = allProducts.find(p => p.id === productId);
    if (!product) return;

    setIsChangingProduct(true);
    try {
      await opportunitiesService.updateOpportunity(opportunity.id, {
        productId: product.id,
        product: {
          id: product.id,
          key: product.key,
          name: product.name,
          type: product.type,
          serviceCategory: serviceCategoryOf(product),
          leadStages: product.leadStages,
          opportunityStages: product.opportunityStages,
          pricing: product.pricing
        } as any,
        valueSource: 'product' as any
      } as any);
      
      // Update local state
      (opportunity as any).productId = product.id;
      (opportunity as any).product = product;
      setSelectedProductId(productId);
      
    } catch (e) {
      console.error('Set product failed', e);
      alert('Failed to set product.');
    } finally {
      setIsChangingProduct(false);
    }
  };

  // ─── Pricing recompute (rate card level × days / package / recurring term) ───
  const pricingComps = opportunity?.product ? pricingComponentsFor(opportunity.product) : [];
  const isRecurringPricing = opportunity?.product
    ? (serviceCategoryOf(opportunity.product) === 'managed_service' || opportunity.product.pricing?.model === 'recurring')
    : false;

  const computeOppValue = (): number | undefined => {
    const prod = opportunity?.product;
    if (!prod) return undefined;
    const p = prod.pricing;
    if (isRecurringPricing) {
      const base = p?.amount ?? p?.range?.max;
      if (base == null) return p?.setupFee || undefined;
      const m = Math.max(1, Number(pricingMonths) || 1);
      const period = p?.billingPeriod || 'month';
      let rec = base * m;
      if (period === 'year') rec = base * (m / 12);
      else if (period === 'quarter') rec = base * (m / 3);
      else if (period === 'week') rec = base * (m * 4);
      return rec + (p?.setupFee || 0);
    }
    if (pricingComps.length > 0) {
      const c = pricingComps[Math.min(pricingBasisIdx, pricingComps.length - 1)];
      const q = c.component === 'day_rate' ? Math.max(1, Number(pricingUnits) || 1) : 1;
      return c.unitPrice * q;
    }
    return p?.amount ?? p?.range?.max ?? undefined;
  };

  const applyPricingValue = async () => {
    if (!opportunity) return;
    const v = computeOppValue();
    if (v == null) return;
    const rounded = Math.round(v);
    setApplyingValue(true);
    try {
      const updates: any = { value: rounded, valueSource: 'product' };
      if (isRecurringPricing) updates.billingTermMonths = Math.max(1, Number(pricingMonths) || 1);
      await opportunitiesService.updateOpportunity(opportunity.id, updates);
      // Reflect locally (mirrors handleProductChange's local-mutation pattern).
      (opportunity as any).value = rounded;
      (opportunity as any).valueSource = 'product';
      if (isRecurringPricing) (opportunity as any).billingTermMonths = updates.billingTermMonths;
      onUpdateOpportunity?.(opportunity.id, updates);
      toastService.success('Opportunity-Wert aus Produktpreis aktualisiert');
    } catch (e) {
      console.error('Apply pricing value failed', e);
      toastService.error('Wert konnte nicht aktualisiert werden');
    } finally {
      setApplyingValue(false);
    }
  };

  const handleSave = async () => {
    if (!onUpdateOpportunity) return setIsEditing(false);
    setSaving(true);
    try {
      const updates: Partial<Opportunity> = {
        title: editData.title.trim(),
        value: editData.value.trim() ? Number(editData.value) : undefined,
        probability: Number(editData.probability) || 0,
        expectedCloseDate: new Date(editData.expectedCloseDate)
      } as any;
      
      // Update company if changed
      if (editData.companyId && editData.companyId !== opportunity.companyId) {
        const selectedCompany = allCompanies.find(c => c.id === editData.companyId);
        if (selectedCompany) {
          updates.companyId = selectedCompany.id;
          updates.company = selectedCompany;
        }
      }

      // Update owner if changed
      if (editData.assignedTo && editData.assignedTo !== opportunity.assignedTo) {
        updates.assignedTo = editData.assignedTo;
        const selectedUser = allUsers.find(u => u.id === editData.assignedTo);
        updates.assignedToName = selectedUser?.name || selectedUser?.email || '';
      }

      // Update business area — empty selection clears the field (null → deleteField in service)
      if (editData.businessArea !== (opportunity.businessArea || '')) {
        updates.businessArea = (editData.businessArea as BusinessArea) || null;
      }
      
      await onUpdateOpportunity(opportunity.id, updates);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (content: string, type: 'general' | 'strategy' | 'concern' | 'competitive') => {
    try {
      await opportunitiesService.addNoteToOpportunity(
        opportunity.id,
        content,
        type,
        currentUserId
      );
      
      // Update local opportunity object
      const newNote = {
        id: `note_${Date.now()}`,
        content,
        type,
        createdAt: new Date(),
        createdBy: currentUserId
      };
      
      if (!opportunity.notes) {
        opportunity.notes = [];
      }
      opportunity.notes.push(newNote);
      
      // Force re-render
      setActiveTab('notes');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    }
  };

  // Inline mode: Render as side panel without full-screen overlay
  if (inline) {
    return (
      <>
        {/* Mobile Backdrop (only on mobile) */}
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        
        {/* Side Panel */}
        <div className={`
          fixed top-0 right-0 h-full w-full sm:w-[500px] lg:w-[600px] max-w-full
          bg-bg-card shadow-heavy lg:shadow-card lg:border-l lg:border-default 
          overflow-y-auto z-50 lg:z-30
          transform transition-transform duration-300 ease-out
        `}>
          {/* Close Button Header */}
          <div className="sticky top-0 bg-bg-card backdrop-blur-sm border-b border-subtle px-4 py-3 z-10 flex items-center justify-between">
            <h2 className="text-lg font-bold text-txt truncate">{opportunity.title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-txt-secondary hover:text-txt" />
            </button>
          </div>
          
          {/* Opportunity Content - reuse the same structure but in a more compact way */}
          <div className="p-4 space-y-4">
            {/* Stage & Value */}
            <div className="flex items-center justify-between">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(opportunity.stage)}`}>
                {opportunity.stage.charAt(0).toUpperCase() + opportunity.stage.slice(1)}
              </span>
              <span className="text-xl font-bold text-txt">{formatCurrency(opportunity.value)}</span>
            </div>
            
            {/* Company & Probability */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-bg-surface rounded-lg p-3">
                <div className="flex items-center text-txt-tertiary text-sm mb-1">
                  <Building2 className="w-4 h-4 mr-1.5" />
                  Company
                </div>
                <p className="font-medium text-txt">{opportunity.company?.name || 'No company'}</p>
              </div>
              <div className="bg-bg-surface rounded-lg p-3">
                <div className="flex items-center text-txt-tertiary text-sm mb-1">
                  <TrendingUp className="w-4 h-4 mr-1.5" />
                  Probability
                </div>
                <p className="font-medium text-txt">{opportunity.probability}%</p>
              </div>
            </div>
            
            {/* Owner & Business Area */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-bg-surface rounded-lg p-3">
                <div className="flex items-center text-txt-tertiary text-sm mb-1">
                  <User className="w-4 h-4 mr-1.5" />
                  Owner
                </div>
                <p className="font-medium text-txt">{opportunity.assignedToName || '-'}</p>
              </div>
              <div className="bg-bg-surface rounded-lg p-3">
                <div className="flex items-center text-txt-tertiary text-sm mb-1">
                  <Briefcase className="w-4 h-4 mr-1.5" />
                  Business Area
                </div>
                <p className="font-medium text-txt">
                  {opportunity.businessArea 
                    ? (BUSINESS_AREAS.find(a => a.key === opportunity.businessArea)?.labelDe || opportunity.businessArea) 
                    : '-'}
                </p>
              </div>
            </div>

            {/* Expected Close Date */}
            <div className="bg-bg-surface rounded-lg p-3">
              <div className="flex items-center text-txt-tertiary text-sm mb-1">
                <Calendar className="w-4 h-4 mr-1.5" />
                Expected Close
              </div>
              <InlineDatePicker
                value={opportunity.expectedCloseDate}
                onChange={async (date) => {
                  if (onUpdateOpportunity) {
                    await onUpdateOpportunity(opportunity.id, { expectedCloseDate: date } as any);
                  }
                }}
                className="font-medium text-txt"
                icon={null}
                disabled={!onUpdateOpportunity}
              />
            </div>
            
            {/* Stage Selector */}
            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Change Stage</label>
              <div className="flex flex-wrap gap-2">
                {stages.map(stage => (
                  <button
                    key={stage.key}
                    onClick={() => onStageChange(opportunity.id, stage.key as Opportunity['stage'])}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      opportunity.stage === stage.key
                        ? 'bg-accent text-txt-inverted'
                        : 'bg-bg-surface text-txt-secondary hover:bg-bg-surface-hover'
                    }`}
                  >
                    {stage.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-default">
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-3 py-2 bg-bg-surface text-txt-secondary rounded-lg hover:bg-bg-surface-hover text-sm font-medium"
              >
                <Edit className="w-4 h-4 mr-1.5" />
                Edit
              </button>
              {onCreateOffer && (
                <button
                  onClick={() => onCreateOffer(opportunity)}
                  className="flex items-center px-3 py-2 bg-success-soft text-success rounded-lg hover:bg-success-soft text-sm font-medium"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Create Offer
                </button>
              )}
              {onUploadDocument && (
                <button
                  onClick={() => onUploadDocument(opportunity)}
                  className="flex items-center px-3 py-2 bg-accent-soft text-accent rounded-lg hover:bg-accent-soft text-sm font-medium"
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Dokument
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Default: Full modal with overlay
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-start sm:items-center justify-center p-2 sm:p-4">
        <div className="relative bg-bg-card backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-heavy w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto transform transition-all mt-2 sm:mt-0">
          {/* Clean Header - Insights Style */}
          <div className="sticky top-0 bg-bg-card backdrop-blur-sm border-b border-subtle px-8 py-6 rounded-t-xl lg:rounded-t-2xl z-10">
            <div className="flex items-center justify-between">
              <div>
                {isEditing ? (
                  <input value={editData.title} onChange={(e)=>setEditData({...editData,title:e.target.value})} className="text-2xl font-bold text-txt border-0 bg-bg-surface rounded-lg px-3 py-1 focus:ring-2 focus:ring-accent/30" />
                ) : (
                  <h2 className="text-2xl font-bold text-txt">{opportunity.title}</h2>
                )}
                <p className="text-sm text-txt-secondary mt-1">
                  {opportunity.company?.name || 'Unknown Company'} {opportunity.value !== undefined && `• ${formatCurrency(opportunity.value)}`}
                </p>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                {onCreateOffer && (
                  <button
                    onClick={() => onCreateOffer(opportunity)}
                    className="px-3 py-2 bg-gradient-to-r from-accent to-accent hover:from-accent hover:to-accent text-txt-inverted rounded-lg transition-all flex items-center shadow-card hover:shadow-card"
                    title="Create offer from this opportunity"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Create Offer</span>
                    <span className="sm:hidden">Offer</span>
                  </button>
                )}
                {onUploadDocument && (
                  <button
                    onClick={() => onUploadDocument(opportunity)}
                    className="px-3 py-2 bg-accent-soft text-accent rounded-lg hover:bg-accent-medium transition-all flex items-center"
                    title="Dokument zu dieser Opportunity hochladen"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Dokument</span>
                    <span className="sm:hidden">Datei</span>
                  </button>
                )}
                {onUpdateOpportunity && orgId && !opportunity.engagementId && (
                  <button
                    onClick={async () => {
                      try {
                        const id = await engagementsService.createFromOpportunity(orgId, opportunity);
                        onUpdateOpportunity(opportunity.id, { engagementId: id });
                        toastService.success('Auftrag/Projekt angelegt und mit der Verkaufschance verknüpft.');
                      } catch (err) {
                        console.error('Create engagement from opportunity failed:', err);
                        toastService.error('Auftrag konnte nicht angelegt werden.');
                      }
                    }}
                    className={`px-3 py-2 rounded-lg transition-all flex items-center text-sm font-medium ${
                      opportunity.stage === 'won'
                        ? 'bg-dev text-txt-inverted hover:bg-dev/90 shadow-card'
                        : 'bg-dev-soft text-dev hover:bg-dev-medium'
                    }`}
                    title="Auftrag/Projekt aus dieser Verkaufschance anlegen"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">{opportunity.stage === 'won' ? 'Auftrag jetzt anlegen' : 'Auftrag anlegen'}</span>
                    <span className="sm:hidden">Auftrag</span>
                  </button>
                )}
                {opportunity.engagementId && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-view', { detail: 'engagements' }))}
                    className="px-3 py-2 bg-dev-soft text-dev rounded-lg hover:bg-dev-medium transition-all flex items-center text-sm font-medium"
                    title="Verknüpften Auftrag öffnen"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Auftrag öffnen</span>
                    <span className="sm:hidden">Auftrag</span>
                  </button>
                )}
                <select
                  value={opportunity.stage}
                  onChange={(e) => {
                    const newStage = e.target.value as Opportunity['stage'];
                    onStageChange(opportunity.id, newStage);
                    opportunity.stage = newStage;
                  }}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm ${getStageColor(opportunity.stage)} border-0 focus:ring-2 focus:ring-accent/30`}
                >
                  {stages.map(stage => (
                    <option key={stage.key} value={stage.key}>{stage.label}</option>
                  ))}
                </select>
                {isEditing ? (
                  <button onClick={handleSave} disabled={saving} className="px-3 py-2 bg-success text-txt-inverted rounded-lg hover:bg-success/80 disabled:opacity-60 flex items-center">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </button>
                ) : (
                  <button onClick={()=>setIsEditing(true)} className="px-3 py-2 bg-bg-surface text-txt-secondary rounded-lg hover:bg-bg-surface-hover flex items-center">
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-txt-secondary hover:text-txt" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-bg-surface rounded-lg p-1 mt-4 sm:mt-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Overview
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'timeline'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Timeline
              </button>
              <button
                onClick={() => setActiveTab('leads')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'leads'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Leads ({opportunity.summary?.totalLeads || leadsInOpportunity.length})
              </button>
              <button
                onClick={() => setActiveTab('company')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'company'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Company
              </button>
              <button
                onClick={() => setActiveTab('product')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'product'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <Package className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Product
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'notes'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Notes ({opportunity.notes?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`flex items-center px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'tasks'
                    ? 'bg-bg-card text-accent shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <ListChecks className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Aufgaben ({oppTasks.length})
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4 sm:space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-info-soft rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                      <span className="text-xs text-accent font-semibold">VALUE</span>
                    </div>
                    {isEditing ? (
                      <input type="number" value={editData.value} onChange={(e)=>setEditData({...editData,value:e.target.value})} className="w-full border rounded-lg px-2 py-1 font-bold text-info" placeholder="Optional" />
                    ) : (
                      <div className="text-lg sm:text-2xl font-bold text-info">
                        {opportunity.value !== undefined ? formatCurrency(opportunity.value) : <span className="text-txt-muted">TBD</span>}
                      </div>
                    )}
                    <div className="text-xs sm:text-sm text-accent">Expected Revenue</div>
                    {opportunity.product && (
                      <div className="text-xs text-txt-tertiary mt-1">
                        Value source: {(opportunity as any).valueSource || 'product'}
                        {(opportunity as any).billingTermMonths && opportunity.product?.pricing?.model === 'recurring' ? ` • ${(opportunity as any).billingTermMonths} months` : ''}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 bg-success-soft rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                      <span className="text-xs text-success font-semibold">PROBABILITY</span>
                    </div>
                    {isEditing ? (
                      <input type="number" value={editData.probability} onChange={(e)=>setEditData({...editData,probability:e.target.value})} className="w-full border rounded-lg px-2 py-1 font-bold text-success" />
                    ) : (
                      <div className="text-lg sm:text-2xl font-bold text-success">{opportunity.probability}%</div>
                    )}
                    <div className="text-xs sm:text-sm text-success">Close Probability</div>
                  </div>
                  
                  <div className="p-4 bg-accent-soft rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                      <span className="text-xs text-accent font-semibold">EXPECTED CLOSE</span>
                    </div>
                    <InlineDatePicker
                      value={opportunity.expectedCloseDate}
                      onChange={async (date) => {
                        if (onUpdateOpportunity) {
                          await onUpdateOpportunity(opportunity.id, { expectedCloseDate: date } as any);
                        }
                        setEditData({ ...editData, expectedCloseDate: toISODateString(date) });
                      }}
                      icon={null}
                      className="text-lg sm:text-2xl font-bold text-accent"
                      renderLabel={() => (
                        <span>{opportunity.expectedCloseDate ? `${Math.ceil((new Date(opportunity.expectedCloseDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}` : 'N/A'}</span>
                      )}
                      disabled={!onUpdateOpportunity}
                    />
                    <div className="text-xs sm:text-sm text-accent">Days to Close</div>
                  </div>
                  
                  <div className="p-4 bg-warning-soft rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                      <span className="text-xs text-warning font-semibold">COMPANY</span>
                    </div>
                    {isEditing ? (
                      <select 
                        value={editData.companyId} 
                        onChange={(e)=>setEditData({...editData,companyId:e.target.value})} 
                        className="w-full border-0 bg-warning-soft rounded-lg px-3 py-2 text-sm font-semibold text-warning focus:ring-2 focus:ring-accent/30"
                      >
                        <option value="">Select Company...</option>
                        {allCompanies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name} {company.domain ? `(${company.domain})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-lg sm:text-2xl font-bold text-warning">{opportunity.company?.name || 'Unknown Company'}</div>
                    )}
                    <div className="text-xs sm:text-sm text-warning">Company Name</div>
                  </div>
                </div>

                {/* Owner & Business Area */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-bg-surface rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary" />
                      <span className="text-xs text-txt-secondary font-semibold">OWNER</span>
                    </div>
                    {isEditing ? (
                      <select 
                        value={editData.assignedTo} 
                        onChange={(e)=>setEditData({...editData,assignedTo:e.target.value})} 
                        className="w-full border-0 bg-bg-surface rounded-lg px-3 py-2 text-sm font-semibold text-txt focus:ring-2 focus:ring-accent/30"
                      >
                        <option value="">Select owner...</option>
                        {allUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-accent-soft flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-accent">
                            {(opportunity.assignedToName || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="text-base sm:text-lg font-bold text-txt truncate">{opportunity.assignedToName || '-'}</div>
                      </div>
                    )}
                    <div className="text-xs sm:text-sm text-txt-secondary mt-1">Verantwortlich</div>
                  </div>
                  
                  <div className="p-4 bg-bg-surface rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary" />
                      <span className="text-xs text-txt-secondary font-semibold">BUSINESS AREA</span>
                    </div>
                    {isEditing ? (
                      <select 
                        value={editData.businessArea} 
                        onChange={(e)=>setEditData({...editData,businessArea:e.target.value})} 
                        className="w-full border-0 bg-bg-surface rounded-lg px-3 py-2 text-sm font-semibold text-txt focus:ring-2 focus:ring-accent/30"
                      >
                        <option value="">Kein Bereich</option>
                        {BUSINESS_AREAS.map(area => (
                          <option key={area.key} value={area.key}>
                            {area.labelDe}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-base sm:text-lg font-bold text-txt">
                        {opportunity.businessArea 
                          ? (BUSINESS_AREAS.find(a => a.key === opportunity.businessArea)?.labelDe || opportunity.businessArea) 
                          : '-'}
                      </div>
                    )}
                    <div className="text-xs sm:text-sm text-txt-secondary mt-1">Geschäftsbereich</div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="bg-bg-surface rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-txt mb-4">Company Information</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Building className="w-4 h-4 sm:w-5 sm:h-5 text-txt-muted flex-shrink-0" />
                        <div>
                          <p className="text-xs sm:text-sm text-txt-tertiary">Company</p>
                          <p className="text-sm sm:text-base font-medium text-txt">{opportunity.company?.name || 'Unknown Company'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-txt-muted flex-shrink-0" />
                        <div>
                          <p className="text-xs sm:text-sm text-txt-tertiary">Industry</p>
                          <p className="text-sm sm:text-base font-medium text-txt">{opportunity.company?.industry || 'Not specified'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-txt-muted flex-shrink-0" />
                        <div>
                          <p className="text-xs sm:text-sm text-txt-tertiary">Company Size</p>
                          <p className="text-sm sm:text-base font-medium text-txt">{opportunity.company?.employeeCount ? `${opportunity.company.employeeCount} employees` : 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Globe className="w-5 h-5 text-txt-muted" />
                        <div>
                          <p className="text-sm text-txt-tertiary">Website</p>
                          <p className="font-medium text-txt">{opportunity.company?.website || 'Not available'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Euro className="w-5 h-5 text-txt-muted" />
                        <div>
                          <p className="text-sm text-txt-tertiary">Revenue</p>
                          <p className="font-medium text-txt">{opportunity.company?.revenue || 'Not available'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-txt-muted" />
                        <div>
                          <p className="text-sm text-txt-tertiary">Phone</p>
                          <p className="font-medium text-txt">{opportunity.company?.phone || 'Not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Summary */}
                {opportunity.summary && (
                  <div className="bg-bg-card border border-default rounded-xl p-6">
                    <h3 className="text-lg font-bold text-txt mb-4">Lead Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-4 bg-bg-surface rounded-lg">
                        <div className="text-2xl font-bold text-txt">{opportunity.summary.averageScore || 0}%</div>
                        <div className="text-sm text-txt-secondary">Average Score</div>
                      </div>
                      <div className="text-center p-4 bg-bg-surface rounded-lg">
                        <div className="text-2xl font-bold text-txt">{opportunity.summary.highestScore || 0}%</div>
                        <div className="text-sm text-txt-secondary">Highest Score</div>
                      </div>
                      <div className="text-center p-4 bg-bg-surface rounded-lg">
                        <div className="text-2xl font-bold text-txt">{opportunity.summary.contactPoints || 0}</div>
                        <div className="text-sm text-txt-secondary">Touch Points</div>
                      </div>
                    </div>
                    
                    {opportunity.summary.keyDecisionMakers && opportunity.summary.keyDecisionMakers.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-txt mb-2">Key Decision Makers</h4>
                        <div className="flex flex-wrap gap-2">
                          {opportunity.summary.keyDecisionMakers.map((person, index) => (
                            <span key={index} className="px-3 py-1 bg-info-soft text-info rounded-full text-sm font-medium">
                              {person}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {opportunity.notes && opportunity.notes.length > 0 && (
                  <div className="bg-bg-card border border-default rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-txt">Notes</h3>
                      <button className="flex items-center px-3 py-1.5 text-sm text-accent hover:bg-info-soft rounded-lg transition-colors">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Note
                      </button>
                    </div>
                    <div className="space-y-3">
                      {opportunity.notes.map((note) => (
                        <div key={note.id} className="p-4 bg-bg-surface rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-txt">{note.createdBy}</span>
                            <span className="text-xs text-txt-tertiary">{formatDateSafe(note.createdAt)}</span>
                          </div>
                          <p className="text-sm text-txt-secondary">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  {(opportunity.activities || []).map((activity, index) => {
                    const ActivityIcon = getActivityIcon(activity.type);
                    const isLast = index === (opportunity.activities || []).length - 1;
                    
                    return (
                      <div key={activity.id} className="relative">
                        {!isLast && (
                          <div className="absolute left-6 top-12 w-0.5 h-full bg-bg-surface-hover -z-10" />
                        )}
                        <div className="flex space-x-4">
                          <div className="flex-shrink-0 w-12 h-12 bg-bg-surface rounded-full flex items-center justify-center">
                            <ActivityIcon className="w-5 h-5 text-txt-secondary" />
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium text-txt">{activity.description}</p>
                              <span className="text-sm text-txt-tertiary">
                                {formatDateSafe(activity.createdAt)}
                              </span>
                            </div>
                            <div className="text-sm text-txt-secondary">
                              by {activity.createdBy}
                              {activity.leadName && (
                                <span> • {activity.leadName}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-txt">Leads in this Opportunity</h3>
                  <button 
                    onClick={() => setShowAddLeadModal(true)}
                    className="flex items-center px-4 py-2 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright transition-colors">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Lead
                  </button>
                </div>
                
                <div className="space-y-3">
                  {leadsInOpportunity.map((lead) => (
                    <div key={lead.id} className="p-4 border border-default rounded-xl hover:bg-bg-base transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-accent to-info flex items-center justify-center">
                            <span className="text-accent font-semibold text-sm">
                              {lead.firstName[0]}{lead.lastName[0]}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-txt">{lead.firstName} {lead.lastName}</h4>
                            <p className="text-xs text-txt-secondary">{lead.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className="px-3 py-1 bg-info-soft text-info rounded-full text-sm font-medium">
                            {lead.score}% score
                          </span>
                          <span className="px-3 py-1 bg-bg-surface text-txt rounded-full text-sm font-medium">
                            {lead.stage}
                          </span>
                          <button className="p-2 hover:bg-bg-surface rounded-lg transition-colors">
                            <ChevronRight className="w-4 h-4 text-txt-muted" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {leadsInOpportunity.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-txt-muted mx-auto mb-4" />
                      <p className="text-txt-tertiary font-medium">No leads added yet</p>
                      <p className="text-sm text-txt-muted mb-4">Add leads to this opportunity to track contacts</p>
                      <button
                        onClick={() => setShowAddLeadModal(true)}
                        className="flex items-center px-4 py-2 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright transition-colors mx-auto"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Lead
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Company Tab */}
            {activeTab === 'company' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-txt">Company Information</h3>
                </div>

                {opportunity.company ? (
                  <div className="bg-bg-card border border-default rounded-2xl overflow-hidden">
                    {/* Company Header */}
                    <div className="bg-gradient-to-br from-accent to-info p-6 border-b border-subtle">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          {/* Company Logo */}
                          <div className="w-16 h-16 bg-bg-card rounded-xl shadow-card flex items-center justify-center overflow-hidden">
                            {opportunity.company.branding?.logoUrl ? (
                            <img 
                              src={opportunity.company.branding.logoUrl} 
                              alt={opportunity.company.name}
                              className="w-full h-full object-contain p-2"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div 
                              className="w-full h-full flex items-center justify-center"
                              style={{
                                backgroundColor: opportunity.company.branding?.brandColor || '#3B82F6'
                              }}
                            >
                              <Building2 className="w-8 h-8 text-txt-inverted" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-txt">{opportunity.company.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-3 py-1 bg-accent text-txt-inverted text-xs font-semibold rounded-full">
                              {opportunity.company.industry || 'Industry not specified'}
                            </span>
                            {opportunity.company.isCustomer && (
                              <span className="px-3 py-1 bg-success-soft text-success text-xs font-semibold rounded-full">
                                Customer
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Company Details Grid */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* General Information */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-txt flex items-center gap-2 mb-3">
                          <Building className="w-5 h-5 text-accent" />
                          General Information
                        </h4>
                        
                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Building className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Company Name</p>
                            <p className="text-sm font-medium text-txt">{opportunity.company.name}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <TrendingUp className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Industry</p>
                            <p className="text-sm font-medium text-txt">{opportunity.company.industry || 'Not specified'}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Users className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Company Size</p>
                            <p className="text-sm font-medium text-txt">
                              {opportunity.company.employeeCount ? `${opportunity.company.employeeCount} employees` : 'Not specified'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Euro className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Revenue</p>
                            <p className="text-sm font-medium text-txt">{opportunity.company.revenue || 'Not available'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-txt flex items-center gap-2 mb-3">
                          <Phone className="w-5 h-5 text-accent" />
                          Contact Details
                        </h4>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Globe className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Website</p>
                            {opportunity.company.website ? (
                              <a 
                                href={opportunity.company.website.startsWith('http') ? opportunity.company.website : `https://${opportunity.company.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-accent hover:text-accent hover:underline"
                              >
                                {opportunity.company.website}
                              </a>
                            ) : (
                              <p className="text-sm text-txt-muted">Not available</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Mail className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Email</p>
                            {opportunity.company.domain ? (
                              <a 
                                href={`mailto:info@${opportunity.company.domain}`}
                                className="text-sm font-medium text-accent hover:text-accent hover:underline"
                              >
                                info@{opportunity.company.domain}
                              </a>
                            ) : (
                              <p className="text-sm text-txt-muted">Not available</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                          <Phone className="w-5 h-5 text-txt-muted flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-txt-tertiary">Phone</p>
                            {opportunity.company.phone ? (
                              <a 
                                href={`tel:${opportunity.company.phone}`}
                                className="text-sm font-medium text-accent hover:text-accent hover:underline"
                              >
                                {opportunity.company.phone}
                              </a>
                            ) : (
                              <p className="text-sm text-txt-muted">Not available</p>
                            )}
                          </div>
                        </div>

                        {opportunity.company.address && (
                          <div className="flex items-center space-x-3 p-3 bg-bg-surface rounded-lg">
                            <MapPin className="w-5 h-5 text-txt-muted flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs text-txt-tertiary">Address</p>
                              <p className="text-sm font-medium text-txt">
                                {opportunity.company.address.street && `${opportunity.company.address.street}, `}
                                {opportunity.company.address.city}
                                {opportunity.company.address.postalCode && ` ${opportunity.company.address.postalCode}`}
                                {opportunity.company.address.country && `, ${opportunity.company.address.country}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Company Description */}
                    {opportunity.company.description && (
                      <div className="mt-6 p-4 bg-info-soft rounded-xl border border-subtle">
                        <h4 className="font-semibold text-txt mb-2">About</h4>
                        <p className="text-sm text-txt-secondary leading-relaxed">{opportunity.company.description}</p>
                      </div>
                    )}

                    {/* Enrichment Data if available */}
                    {opportunity.company.enrichment && (
                      <div className="mt-6 space-y-4">
                        {opportunity.company.enrichment.keyPeople && opportunity.company.enrichment.keyPeople.length > 0 && (
                          <div className="p-4 bg-bg-surface rounded-xl">
                            <h4 className="font-semibold text-txt mb-3">Key People</h4>
                            <div className="space-y-2">
                              {opportunity.company.enrichment.keyPeople.map((person, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 bg-bg-card rounded-lg">
                                  <div className="w-8 h-8 bg-info-soft rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-accent" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-txt">{person.name}</p>
                                    <p className="text-xs text-txt-secondary">{person.position}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {opportunity.company.enrichment.competitors && opportunity.company.enrichment.competitors.length > 0 && (
                          <div className="p-4 bg-bg-surface rounded-xl">
                            <h4 className="font-semibold text-txt mb-3">Competitors</h4>
                            <div className="flex flex-wrap gap-2">
                              {opportunity.company.enrichment.competitors.map((competitor, idx) => (
                                <span key={idx} className="px-3 py-1.5 bg-bg-card text-txt-secondary rounded-lg text-sm font-medium border border-default">
                                  {competitor}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {opportunity.company.enrichment.tags && opportunity.company.enrichment.tags.length > 0 && (
                          <div className="p-4 bg-bg-surface rounded-xl">
                            <h4 className="font-semibold text-txt mb-3">Tags</h4>
                            <div className="flex flex-wrap gap-2">
                              {opportunity.company.enrichment.tags.map((tag, idx) => (
                                <span key={idx} className="px-3 py-1 bg-info-soft text-accent rounded-full text-xs font-medium">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-bg-card border border-default rounded-2xl p-8 text-center">
                  <Building2 className="w-12 h-12 text-txt-muted mx-auto mb-4" />
                  <p className="text-txt-secondary">No company information available</p>
                </div>
              )}
              </div>
            )}

            {/* Product Tab */}
            {activeTab === 'product' && (
              <div className="space-y-6">
                {/* Product Selection Dropdown */}
                <div className="bg-bg-card border border-default rounded-xl p-6">
                  <label className="block text-sm font-semibold text-txt mb-3">
                    Select Product / Service
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-txt-muted" />
                    <select
                      value={selectedProductId}
                      onChange={(e) => handleProductChange(e.target.value)}
                      disabled={isChangingProduct}
                      className="w-full pl-12 pr-4 py-3 border-2 border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent text-sm font-medium appearance-none bg-bg-card disabled:opacity-50"
                    >
                      <option value="">No product selected</option>
                      {allProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.type})
                        </option>
                      ))}
                    </select>
                    {isChangingProduct && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-accent border-t-accent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-txt-secondary">
                    {allProducts.length === 0 
                      ? 'No products available. Create products in Products & Services.'
                      : 'Changing the product will update the sales stages for this opportunity.'
                    }
                  </p>
                </div>

                {opportunity.product ? (
                  <div className="bg-bg-card border border-default rounded-2xl overflow-hidden">
                    {/* Product Header */}
                    <div className="bg-gradient-to-br from-accent to-info p-6 border-b border-subtle">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-bg-card rounded-xl shadow-card flex items-center justify-center">
                          {opportunity.product.type === 'consultancy' ? (
                            <Users className="w-8 h-8 text-accent" />
                          ) : opportunity.product.type === 'development' ? (
                            <Code className="w-8 h-8 text-accent" />
                          ) : opportunity.product.type === 'service' ? (
                            <Wrench className="w-8 h-8 text-accent" />
                          ) : (
                            <Package className="w-8 h-8 text-accent" />
                          )}
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-txt">{opportunity.product.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-3 py-1 bg-accent text-txt-inverted text-xs font-semibold rounded-full capitalize">
                              {opportunity.product.type}
                            </span>
                            {opportunity.product.pricing?.model && (
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                opportunity.product.pricing.model === 'recurring'
                                  ? 'bg-success-soft text-success'
                                  : 'bg-info-soft text-accent'
                              }`}>
                                {opportunity.product.pricing.model === 'recurring' ? 'Recurring' : 'One-time'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product Details Grid */}
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Pricing Section */}
                        <div className="space-y-4">
                          <h4 className="font-semibold text-txt flex items-center gap-2">
                            <Euro className="w-5 h-5 text-accent" />
                            Pricing Details
                          </h4>
                          
                          {opportunity.product.pricing ? (
                            <div className="space-y-3">
                              {opportunity.product.pricing.amount ? (
                                <div className="flex items-center justify-between p-3 bg-accent-soft rounded-lg">
                                  <span className="text-sm text-txt-secondary">Price</span>
                                  <span className="text-lg font-bold text-accent">
                                    {opportunity.product.pricing.currency || 'EUR'} {opportunity.product.pricing.amount.toLocaleString()}
                                    {opportunity.product.pricing.billingPeriod && (
                                      <span className="text-sm font-normal text-txt-secondary"> / {opportunity.product.pricing.billingPeriod}</span>
                                    )}
                                  </span>
                                </div>
                              ) : opportunity.product.pricing.range ? (
                                <div className="flex items-center justify-between p-3 bg-accent-soft rounded-lg">
                                  <span className="text-sm text-txt-secondary">Price Range</span>
                                  <span className="text-lg font-bold text-accent">
                                    {opportunity.product.pricing.currency || 'EUR'} {opportunity.product.pricing.range.min.toLocaleString()} – {opportunity.product.pricing.range.max.toLocaleString()}
                                    {opportunity.product.pricing.billingPeriod && (
                                      <span className="text-sm font-normal text-txt-secondary"> / {opportunity.product.pricing.billingPeriod}</span>
                                    )}
                                  </span>
                                </div>
                              ) : null}

                              <div className="flex items-center justify-between p-3 bg-bg-surface rounded-lg">
                                <span className="text-sm text-txt-secondary">Model</span>
                                <span className="text-sm font-semibold text-txt capitalize">
                                  {opportunity.product.pricing.model}
                                </span>
                              </div>

                              {/* Setup fee */}
                              {!!opportunity.product.pricing.setupFee && (
                                <div className="flex items-center justify-between p-3 bg-bg-surface rounded-lg">
                                  <span className="text-sm text-txt-secondary">Setup-Gebühr (einmalig)</span>
                                  <span className="text-sm font-semibold text-txt">
                                    {formatCurrency(opportunity.product.pricing.setupFee)}
                                  </span>
                                </div>
                              )}

                              {/* Rate cards */}
                              {opportunity.product.pricing.rateCards && opportunity.product.pricing.rateCards.length > 0 && (
                                <div className="p-3 bg-bg-surface rounded-lg">
                                  <p className="text-sm text-txt-secondary mb-2">Tagessätze</p>
                                  <div className="space-y-1">
                                    {opportunity.product.pricing.rateCards.map((rc, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-txt">{rc.level}</span>
                                        <span className="font-semibold text-txt">{formatCurrency(rc.dayRate)} / Tag</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Fixed packages */}
                              {opportunity.product.pricing.fixedPackages && opportunity.product.pricing.fixedPackages.length > 0 && (
                                <div className="p-3 bg-bg-surface rounded-lg">
                                  <p className="text-sm text-txt-secondary mb-2">Pakete</p>
                                  <div className="space-y-1">
                                    {opportunity.product.pricing.fixedPackages.map((pk, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <span className="text-txt">{pk.name}{pk.description ? <span className="text-txt-tertiary"> — {pk.description}</span> : ''}</span>
                                        <span className="font-semibold text-txt whitespace-nowrap">{formatCurrency(pk.price)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Recompute opportunity value from pricing */}
                              {(isRecurringPricing || pricingComps.length > 0) && (
                                <div className="p-3 border border-dashed border-accent/40 bg-accent-soft/40 rounded-lg space-y-2">
                                  <p className="text-sm font-medium text-txt-secondary">Wert aus Preis berechnen</p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    {isRecurringPricing ? (
                                      <>
                                        <label className="text-xs text-txt-secondary">Laufzeit (Monate)</label>
                                        <input
                                          type="number"
                                          min={1}
                                          value={pricingMonths}
                                          onChange={(e) => setPricingMonths(Math.max(1, Number(e.target.value) || 1))}
                                          className="w-24 px-2 py-1.5 border border-strong rounded-lg bg-bg-card text-sm"
                                        />
                                        {!!opportunity.product.pricing.setupFee && (
                                          <span className="text-xs text-txt-tertiary">inkl. Setup</span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          value={Math.min(pricingBasisIdx, pricingComps.length - 1)}
                                          onChange={(e) => setPricingBasisIdx(Number(e.target.value))}
                                          className="px-2 py-1.5 border border-strong rounded-lg bg-bg-card text-sm"
                                        >
                                          {pricingComps.map((c, i) => (
                                            <option key={i} value={i}>{c.label} — {formatCurrency(c.unitPrice)}</option>
                                          ))}
                                        </select>
                                        {pricingComps[Math.min(pricingBasisIdx, pricingComps.length - 1)]?.component === 'day_rate' && (
                                          <>
                                            <label className="text-xs text-txt-secondary">Tage</label>
                                            <input
                                              type="number"
                                              min={1}
                                              value={pricingUnits}
                                              onChange={(e) => setPricingUnits(Math.max(1, Number(e.target.value) || 1))}
                                              className="w-20 px-2 py-1.5 border border-strong rounded-lg bg-bg-card text-sm"
                                            />
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-txt-secondary">
                                      Ergebnis: <span className="font-bold text-txt">{formatCurrency(computeOppValue() || 0)}</span>
                                    </span>
                                    <button
                                      onClick={applyPricingValue}
                                      disabled={applyingValue || computeOppValue() == null}
                                      className="px-3 py-1.5 bg-accent hover:bg-accent-bright text-txt-inverted rounded-lg text-sm transition-colors disabled:opacity-50"
                                    >
                                      {applyingValue ? 'Übernehme…' : 'Als Wert übernehmen'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-txt-tertiary">No pricing information available</p>
                          )}
                        </div>

                        {/* Stages Section */}
                        <div className="space-y-4">
                          <h4 className="font-semibold text-txt flex items-center gap-2">
                            <Layers className="w-5 h-5 text-accent" />
                            Sales Stages
                          </h4>
                          
                          {opportunity.product.opportunityStages && opportunity.product.opportunityStages.length > 0 ? (
                            <div className="space-y-2">
                              <div className="p-3 bg-bg-surface rounded-lg">
                                <p className="text-xs text-txt-secondary mb-2">Opportunity Journey</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {opportunity.product.opportunityStages
                                    .filter(s => {
                                      const normalized = s.toLowerCase().trim().replace(/\s+/g, '_');
                                      return !['won', 'lost', 'loss', 'purchase_order', 'contract', 'handoff'].includes(normalized);
                                    })
                                    .map((stage, index) => {
                                      const isCurrent = opportunity.stage.toLowerCase() === stage.toLowerCase();
                                      return (
                                        <span 
                                          key={index} 
                                          className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                            isCurrent 
                                              ? 'bg-accent text-txt-inverted shadow-card'
                                              : 'bg-bg-surface-hover text-txt-secondary'
                                          }`}
                                        >
                                          {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                        </span>
                                      );
                                    })}
                                </div>
                              </div>
                              
                              {opportunity.product.leadStages && opportunity.product.leadStages.length > 0 && (
                                <div className="p-3 bg-bg-surface rounded-lg">
                                  <p className="text-xs text-txt-secondary mb-2">Lead Stages</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {opportunity.product.leadStages
                                      .filter(s => {
                                        const normalized = s.toLowerCase().trim().replace(/\s+/g, '_');
                                        return !['won', 'lost', 'loss', 'purchase_order', 'contract', 'handoff'].includes(normalized);
                                      })
                                      .map((stage, index) => (
                                        <span 
                                          key={index} 
                                          className="px-2.5 py-1 text-xs font-medium bg-bg-surface-hover text-txt-secondary rounded-full"
                                        >
                                          {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-txt-tertiary">No stages defined</p>
                          )}
                        </div>
                      </div>

                      {/* Product Key Info */}
                      <div className="mt-6 p-4 bg-bg-surface rounded-xl">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-xs text-txt-secondary mb-1">Product Key</p>
                            <p className="text-sm font-mono font-semibold text-txt">{opportunity.product.key}</p>
                          </div>
                          <div>
                            <p className="text-xs text-txt-secondary mb-1">Type</p>
                            <p className="text-sm font-semibold text-txt capitalize">{opportunity.product.type}</p>
                          </div>
                          {opportunity.product.opportunityStages && (
                            <div>
                              <p className="text-xs text-txt-secondary mb-1">Opp. Stages</p>
                              <p className="text-sm font-semibold text-txt">{opportunity.product.opportunityStages.length}</p>
                            </div>
                          )}
                          {opportunity.product.leadStages && (
                            <div>
                              <p className="text-xs text-txt-secondary mb-1">Lead Stages</p>
                              <p className="text-sm font-semibold text-txt">{opportunity.product.leadStages.length}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-bg-surface rounded-2xl border-2 border-dashed border-strong">
                    <Package className="w-16 h-16 text-txt-muted mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-txt mb-2">No Product Assigned</h3>
                    <p className="text-sm text-txt-secondary mb-2">Select a product from the dropdown above to assign it to this opportunity</p>
                    <p className="text-xs text-txt-tertiary">This will define pricing and sales stages for this deal</p>
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                <OpportunityNotes 
                  notes={opportunity.notes || []}
                  currentUserId={currentUserId}
                  currentUserName={currentUserName}
                  currentUserPhoto={currentUserPhoto}
                  onAddNote={handleAddNote}
                />
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="space-y-3">
                {oppTasks.length > 0 && (
                  <div className="space-y-1.5">
                    {oppTasks.map(task => (
                      <div key={task.id} className="flex items-center gap-2 px-3 py-2 bg-bg-card rounded-lg border border-default hover:bg-bg-surface transition-colors">
                        <button onClick={async () => {
                          await tasksService.updateTask(task.id, { completed: !task.completed });
                          setOppTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: !t.completed } : t));
                        }} className="flex-shrink-0">
                          {task.completed ? <CheckSquare className="w-4 h-4 text-success" /> : <Square className="w-4 h-4 text-txt-muted" />}
                        </button>
                        <span className={`text-sm flex-1 ${task.completed ? 'line-through text-txt-muted' : 'text-txt font-medium'}`}>{task.title}</span>
                        {task.dueDate && (
                          <span className="text-xs text-txt-muted">{formatDateSafe(task.dueDate)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {oppTasks.length === 0 && (
                  <div className="text-center py-6 text-txt-muted">
                    <ListChecks className="w-8 h-8 mx-auto mb-2 text-txt-muted" />
                    <p className="text-sm">Noch keine Aufgaben verknuepft</p>
                  </div>
                )}
                <QuickAddTask
                  orgId={opportunity.orgId || ''}
                  currentUserId={currentUserId}
                  defaultOpportunityId={opportunity.id}
                  onTaskCreated={async () => {
                    const allTasks = await tasksService.getTasks(currentUserId, opportunity.orgId || '');
                    setOppTasks(allTasks.filter(t => t.opportunityId === opportunity.id));
                  }}
                />
              </div>
            )}
          </div>

          {/* Add Lead Modal */}
          {showAddLeadModal && (
            <div className="fixed inset-0 z-60 overflow-y-auto">
              {/* Overlay */}
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setShowAddLeadModal(false)} />
              
              {/* Modal */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-bg-card rounded-2xl shadow-heavy w-full max-w-2xl transform transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-default">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-info-soft rounded-lg">
                        <UserPlus className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-txt">Add Leads to Opportunity</h3>
                        <p className="text-sm text-txt-secondary">{opportunity.title}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowAddLeadModal(false);
                        setSelectedLeads([]);
                        setSearchTerm('');
                      }}
                      className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-txt-muted" />
                    </button>
                  </div>

                  {/* Search */}
                  <div className="p-6 border-b border-default">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                      <input
                        type="text"
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* Available Leads List */}
                  <div className="p-6 max-h-96 overflow-y-auto">
                    {availableLeads.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-txt-muted mx-auto mb-4" />
                        <p className="text-txt-tertiary font-medium">All leads are already added</p>
                        <p className="text-sm text-txt-muted">This opportunity includes all available leads</p>
                      </div>
                    ) : filteredAvailableLeads.length === 0 ? (
                      <div className="text-center py-8">
                        <Search className="w-12 h-12 text-txt-muted mx-auto mb-4" />
                        <p className="text-txt-tertiary font-medium">No leads found</p>
                        <p className="text-sm text-txt-muted">Try a different search term</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-txt-secondary">
                            {selectedLeads.length > 0 ? `${selectedLeads.length} leads selected` : 'Select leads to add'}
                          </p>
                          {selectedLeads.length > 0 && (
                            <button
                              onClick={() => setSelectedLeads([])}
                              className="text-sm text-txt-tertiary hover:text-txt-secondary transition-colors"
                            >
                              Clear selection
                            </button>
                          )}
                        </div>
                        
                        {filteredAvailableLeads.map((lead) => (
                          <div
                            key={lead.id}
                            onClick={() => toggleLeadSelection(lead.id)}
                            className={`p-4 border rounded-xl cursor-pointer transition-all ${
                              selectedLeads.includes(lead.id)
                                ? 'border-accent bg-info-soft'
                                : 'border-default hover:border-strong hover:bg-bg-surface'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-accent to-info flex items-center justify-center">
                                  <span className="text-accent font-semibold text-sm">
                                    {lead.firstName[0]}{lead.lastName[0]}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-txt">{lead.firstName} {lead.lastName}</h4>
                                  <p className="text-sm text-txt-secondary">{lead.company}</p>
                                  <p className="text-xs text-txt-tertiary">{lead.email}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <span className="px-3 py-1 bg-success-soft text-success rounded-full text-xs font-bold">
                                  {lead.score}%
                                </span>
                                <span className="px-3 py-1 bg-bg-surface text-txt rounded-full text-xs font-medium">
                                  {lead.stage}
                                </span>
                                {selectedLeads.includes(lead.id) && (
                                  <div className="w-6 h-6 bg-accent text-txt-inverted rounded-full flex items-center justify-center">
                                    <Check className="w-3 h-3" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {availableLeads.length > 0 && (
                    <div className="p-6 border-t border-default">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowAddLeadModal(false);
                            setSelectedLeads([]);
                            setSearchTerm('');
                          }}
                          className="flex-1 px-4 py-3 border border-strong text-txt-secondary rounded-xl hover:bg-bg-surface transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddSelectedLeads}
                          disabled={selectedLeads.length === 0}
                          className="flex-1 px-4 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default OpportunityDetail;